import { isAfter, isSameDay } from 'date-fns'
import { FieldValidator } from 'final-form'

function validateHtmlWithAllowedTags(allowedTags: string[], value?: string) {
  if (!value) {
    return undefined
  }

  const allowedTagsSet = new Set(allowedTags)

  const parser = new DOMParser()
  const doc = parser.parseFromString(value, 'text/html')

  if (doc.querySelector('parsererror')) {
    return 'Invalid HTML'
  }

  const elements = doc.body.getElementsByTagName('*')
  const includesNotAllowedTags = Array.from(elements).some((el) => !allowedTagsSet.has(el.tagName))

  if (includesNotAllowedTags) {
    return `Should contain only these HTML-tags: ${allowedTags.join(', ')}`
  }

  return undefined
}

// eslint-disable-next-line react/display-name, @typescript-eslint/no-explicit-any
export function validateRequired(value?: any) {
  if (Array.isArray(value)) {
    return !value.length ? 'Required' : undefined
  }

  return !value ? 'Required' : undefined
}

export function validateRequiredNumber(value?: string) {
  return value == undefined || value === '' ? 'Required' : undefined
}

export function validateRequiredNumberBetween(value: string | undefined, from: number, to: number) {
  const numberError = validateRequiredNumber(value)

  return numberError || (value && (+value < from || +value > to) ? `Must be from ${from} to ${to}` : undefined)
}

export function validateRequiredPercentage(value?: string) {
  const numberError = validateRequiredNumber(value)

  return numberError || (value && (+value < 0 || +value > 100) ? 'Must be from 0 to 100' : undefined)
}

export function validateRequiredWPM(value?: string) {
  const numberError = validateRequiredNumber(value)

  return numberError || (value && (+value < 50 || +value > 500) ? 'Must be from 50 to 500' : undefined)
}

export function validateRequiredPromoCode(promoCode?: string) {
  if (!promoCode) {
    return 'Required'
  }

  const pattern = /^[a-zA-Z0-9]+$/

  return pattern.test(promoCode ?? '') ? undefined : 'Must contain letters and numbers only (A-Za-z0-9)'
}

export function validateRequiredHtml(value?: string) {
  return !value || value === '<p><br></p>' ? 'Required' : undefined
}

// Tags allowed for Editor type="default" (font styles/lists/link; no images/tables/media/formulas)
export function validateDefaultHtml(value?: string) {
  return validateHtmlWithAllowedTags(
    ['P', 'A', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'CODE', 'BR', 'SUB', 'SUP', 'UL', 'OL', 'LI', 'SPAN'],
    value
  )
}

export function validateRequiredDefaultHtml(value?: string) {
  return validateRequiredHtml(value) || validateDefaultHtml(value)
}

export function validateDuration(value?: string) {
  if (!value) {
    return undefined
  }

  return !/^\d?\d:\d\d$/.test(value) ? 'Must be in HH:MM format' : undefined
}

export function validateRequiredDuration(value?: string) {
  return validateRequired(value) || validateDuration(value)
}

export function validateRequiredDurationInMinutes(value?: string) {
  if (!value) {
    return 'Required'
  }

  return !/^\d?\d:\d\d$/.test(value) ? 'Must be in MM:SS format' : undefined
}

export function validateUrl(url: string) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i'
  )
  return pattern.test(url) ? undefined : 'Must be a valid URL'
}

// TODO: use generic and fix it
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validateAnswerFieldArray: FieldValidator<Array<any> | undefined> = (array) => {
  if (!array || !array.length) {
    return 'A question should have at least 1 option with answer'
  }

  const hasCorrectAnswer = array.some(({ isCorrect }) => isCorrect)

  if (!hasCorrectAnswer) {
    return 'Should select a correct answer'
  }

  return null
}

export const validateRequiredField = (value: unknown) => (value ? undefined : 'Required')
export const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const EMAIL_ERROR = 'This is an invalid email'

export const validateEmail = (value: string) => (EMAIL_PATTERN.test(value) ? undefined : EMAIL_ERROR)

export const validateIsNotInFuture = (errorMessage: string) => (value: Date | null) => {
  const currentDate = new Date()
  if (!value) {
    return
  }

  if (isSameDay(value, currentDate)) {
    return
  }

  return isAfter(value, currentDate) ? errorMessage : undefined
}
